/**
 * EF-SETTINGS-CONSOLIDATION-V1 (PHASE 2): Unified Email Configuration Page
 * 
 * This page consolidates all email settings into a single tabbed interface:
 * - Accounts (IMAP/SMTP providers)
 * - Sending Defaults (default sender configuration)
 * - Inbox Settings (polling, auto-fetch, attachments)
 * - Templates (email templates for documents)
 * 
 * SHADOW MODE: This page is controlled by USE_NEW_EMAIL_UI flag.
 * When false, the old separate pages are used.
 * When true, this unified page is shown.
 */

import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api, { 
  extractErrorMessage,
  getEmailTemplates,
  createEmailTemplate,
  updateEmailTemplate,
  deleteEmailTemplate,
  seedDefaultEmailTemplates,
} from "../lib/api";
import { toast } from "sonner";
import { useFormatters } from "../components/shared/useFormatters";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert";
import { Slider } from "../components/ui/slider";
import {
  Mail,
  Plus,
  Pencil,
  Trash2,
  Send,
  CheckCircle2,
  XCircle,
  Loader2,
  Settings,
  History,
  Shield,
  Download,
  TestTube2,
  Inbox,
  Eye,
  EyeOff,
  FileText,
  Star,
  AlertCircle,
  Info,
  Clock,
  RefreshCw,
  Paperclip,
  ChevronRight,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// ============================================================================
// PROVIDER TYPES (from EmailSettingsPage)
// ============================================================================
const PROVIDER_TYPES = [
  { value: "sendgrid", label: "SendGrid", hasApiKey: true },
  { value: "mailgun", label: "Mailgun", hasApiKey: true },
  { value: "ses", label: "Amazon SES", hasApiKey: true },
  { value: "smtp", label: "Custom SMTP", hasSMTP: true },
  { value: "google_smtp", label: "Gmail SMTP (App Password)", hasSMTP: true, hasImap: true },
];

// ============================================================================
// TEMPLATE TYPES (from EmailTemplatesPage)
// ============================================================================
const TEMPLATE_TYPES = [
  { value: "quote", label: "Quote", description: "For sending quotes to clients" },
  { value: "invoice", label: "Invoice", description: "For sending invoices to clients" },
  { value: "reminder", label: "Payment Reminder", description: "For overdue payment reminders" },
];

const TEMPLATE_VARIABLES = [
  { name: "company_name", description: "Your company name" },
  { name: "client_name", description: "Client's name" },
  { name: "document_number", description: "Quote/Invoice number" },
  { name: "total_amount", description: "Total amount" },
  { name: "currency_symbol", description: "Currency symbol (e.g., £, €)" },
  { name: "due_date", description: "Invoice due date" },
  { name: "valid_until", description: "Quote validity date" },
  { name: "payment_terms", description: "Payment terms text" },
  { name: "balance_due", description: "Outstanding balance" },
];

// ============================================================================
// PASSWORD PREVIEW COMPONENT (from EmailSettingsPage)
// ============================================================================
const PasswordPreview = ({ providerId }) => {
  const [visible, setVisible] = useState(false);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchPreview = async () => {
    if (preview) {
      setVisible(!visible);
      return;
    }
    
    setLoading(true);
    try {
      const response = await api.get(`/settings/email/providers/${providerId}/password-preview`);
      setPreview(response.data);
      setVisible(true);
    } catch (error) {
      console.error('Failed to fetch password preview');
    } finally {
      setLoading(false);
    }
  };

  if (!visible || !preview) {
    return (
      <button 
        onClick={fetchPreview}
        className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 mt-1"
        disabled={loading}
      >
        {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Eye className="h-3 w-3" />}
        {loading ? "Loading..." : "Show hint"}
      </button>
    );
  }

  return (
    <div className="mt-1 space-y-1">
      <button 
        onClick={() => setVisible(false)}
        className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
      >
        <EyeOff className="h-3 w-3" />
        Hide
      </button>
      {preview.smtp_password_preview && (
        <p className="text-xs font-mono bg-surface-elevated px-2 py-1 rounded">
          SMTP: {preview.smtp_password_preview}
        </p>
      )}
      {preview.imap_password_preview && (
        <p className="text-xs font-mono bg-surface-elevated px-2 py-1 rounded">
          IMAP: {preview.imap_password_preview}
        </p>
      )}
    </div>
  );
};

// ============================================================================
// ACCOUNTS TAB COMPONENT
// ============================================================================
function AccountsTab({ 
  providers, 
  loading, 
  onRefresh,
  onCreateProvider,
  onEditProvider,
  onDeleteProvider,
  onToggleEnabled,
  onTestProvider,
  onSetDefault,
}) {
  const { formatDateTime } = useFormatters();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (providers.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <Mail className="h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium mb-2">No providers configured</h3>
          <p className="text-muted-foreground text-sm mb-4">
            Add an email provider to start sending notifications
          </p>
          <Button onClick={onCreateProvider}>
            <Plus className="h-4 w-4 mr-2" />
            Add Your First Provider
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={onCreateProvider} data-testid="add-provider-btn-tab">
          <Plus className="h-4 w-4 mr-2" />
          Add Provider
        </Button>
      </div>
      
      <div className="grid gap-4">
        {providers.map((provider) => (
          <Card key={provider.id} className="overflow-hidden">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-md ${provider.enabled ? "bg-primary/10" : "bg-surface-elevated"}`}>
                    <Mail className={`h-5 w-5 ${provider.enabled ? "text-primary" : "text-muted-foreground"}`} />
                  </div>
                  <div>
                    <CardTitle className="text-base flex items-center gap-2">
                      {provider.name}
                      {provider.is_default && (
                        <Badge variant="secondary" className="text-xs">
                          <Shield className="h-3 w-3 mr-1" />
                          Default
                        </Badge>
                      )}
                    </CardTitle>
                    <CardDescription className="text-xs">
                      {PROVIDER_TYPES.find((t) => t.value === provider.provider_type)?.label || provider.provider_type}
                    </CardDescription>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={provider.enabled}
                    onCheckedChange={() => onToggleEnabled(provider)}
                    data-testid={`toggle-${provider.id}`}
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-4">
                <div>
                  <span className="text-muted-foreground">From</span>
                  <p className="font-mono text-xs truncate">{provider.from_email}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">From Name</span>
                  <p className="truncate">{provider.from_name}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Credentials</span>
                  <p className="flex items-center gap-1">
                    {provider.provider_type === "smtp" || provider.provider_type === "google_smtp" ? (
                      provider.smtp_password_set ? (
                        <><CheckCircle2 className="h-3 w-3 text-green-500" /> Configured</>
                      ) : (
                        <><XCircle className="h-3 w-3 text-red-500" /> Not set</>
                      )
                    ) : (
                      provider.api_key_set ? (
                        <><CheckCircle2 className="h-3 w-3 text-green-500" /> Configured</>
                      ) : (
                        <><XCircle className="h-3 w-3 text-red-500" /> Not set</>
                      )
                    )}
                  </p>
                  {(provider.smtp_password_set || provider.imap_password_set) && (
                    <PasswordPreview providerId={provider.id} />
                  )}
                </div>
                <div>
                  <span className="text-muted-foreground">Status</span>
                  <div>
                    <Badge variant={provider.enabled ? "default" : "secondary"}>
                      {provider.enabled ? "Enabled" : "Disabled"}
                    </Badge>
                  </div>
                </div>
                {(provider.provider_type === "google_smtp" || provider.provider_type === "smtp") && (
                  <div className="col-span-2 md:col-span-4 pt-2 border-t border-white/5">
                    <span className="text-muted-foreground">IMAP Receiving</span>
                    <p className="flex items-center gap-1">
                      {provider.imap_host ? (
                        <>
                          <Inbox className="h-3 w-3 text-blue-400" />
                          <span className="text-xs">{provider.imap_host}:{provider.imap_port}</span>
                          {provider.imap_password_set ? (
                            <Badge variant="outline" className="text-xs ml-2">
                              <CheckCircle2 className="h-3 w-3 mr-1 text-green-500" />
                              Configured
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-xs ml-2 text-yellow-500">
                              <AlertCircle className="h-3 w-3 mr-1" />
                              No Password
                            </Badge>
                          )}
                        </>
                      ) : (
                        <span className="text-xs text-muted-foreground">Not configured</span>
                      )}
                    </p>
                  </div>
                )}
              </div>
              <div className="flex flex-wrap gap-2 pt-3 border-t border-white/10">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onEditProvider(provider)}
                  data-testid={`edit-${provider.id}`}
                >
                  <Pencil className="h-3 w-3 mr-1" />
                  Edit
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onTestProvider(provider)}
                  data-testid={`test-${provider.id}`}
                >
                  <Send className="h-3 w-3 mr-1" />
                  Test
                </Button>
                {!provider.is_default && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onSetDefault(provider)}
                    data-testid={`set-default-${provider.id}`}
                  >
                    <Star className="h-3 w-3 mr-1" />
                    Set Default
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-destructive hover:text-destructive hover:bg-destructive/10"
                  onClick={() => onDeleteProvider(provider)}
                  data-testid={`delete-${provider.id}`}
                >
                  <Trash2 className="h-3 w-3 mr-1" />
                  Delete
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// SENDING DEFAULTS TAB COMPONENT
// ============================================================================
function SendingDefaultsTab({ providers, loading, onRefresh }) {
  const defaultProvider = providers.find(p => p.is_default);
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Send className="h-5 w-5 text-primary" />
            Default Sender
          </CardTitle>
          <CardDescription>
            The default email account used for sending system notifications and documents
          </CardDescription>
        </CardHeader>
        <CardContent>
          {defaultProvider ? (
            <div className="flex items-center gap-4 p-4 bg-surface-elevated rounded-lg">
              <div className="p-3 rounded-full bg-primary/10">
                <Mail className="h-6 w-6 text-primary" />
              </div>
              <div className="flex-1">
                <p className="font-medium">{defaultProvider.name}</p>
                <p className="text-sm text-muted-foreground">{defaultProvider.from_email}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {PROVIDER_TYPES.find(t => t.value === defaultProvider.provider_type)?.label}
                </p>
              </div>
              <Badge variant="default" className="bg-green-600">
                <CheckCircle2 className="h-3 w-3 mr-1" />
                Active
              </Badge>
            </div>
          ) : (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>No default sender configured</AlertTitle>
              <AlertDescription>
                Go to the Accounts tab and set a provider as default to enable email sending.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Info className="h-5 w-5 text-primary" />
            Sending Behavior
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-surface-elevated rounded-lg">
            <div>
              <p className="font-medium">Auto-send quotes on approval</p>
              <p className="text-sm text-muted-foreground">Automatically email quotes when they're approved</p>
            </div>
            <Switch disabled />
          </div>
          <div className="flex items-center justify-between p-4 bg-surface-elevated rounded-lg">
            <div>
              <p className="font-medium">Auto-send invoices on creation</p>
              <p className="text-sm text-muted-foreground">Automatically email invoices when they're finalized</p>
            </div>
            <Switch disabled />
          </div>
          <p className="text-xs text-muted-foreground text-center">
            These settings will be available in a future update
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

// ============================================================================
// INBOX SETTINGS TAB COMPONENT (UI ONLY)
// ============================================================================
function InboxSettingsTab() {
  const [pollingInterval, setPollingInterval] = useState(5);
  const [autoFetch, setAutoFetch] = useState(true);
  const [downloadAttachments, setDownloadAttachments] = useState(true);

  return (
    <div className="space-y-6">
      <Alert>
        <Info className="h-4 w-4" />
        <AlertTitle>Inbox Settings Preview</AlertTitle>
        <AlertDescription>
          These settings control how the inbox fetches and processes emails. 
          Changes here are UI previews only and will be functional in a future update.
        </AlertDescription>
      </Alert>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5 text-primary" />
            Polling Settings
          </CardTitle>
          <CardDescription>
            Configure how often to check for new emails
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Auto-fetch emails</p>
                <p className="text-sm text-muted-foreground">Automatically check for new emails periodically</p>
              </div>
              <Switch 
                checked={autoFetch} 
                onCheckedChange={setAutoFetch}
                data-testid="auto-fetch-toggle"
              />
            </div>
            
            {autoFetch && (
              <div className="space-y-3 pt-4 border-t">
                <div className="flex items-center justify-between">
                  <Label>Polling interval</Label>
                  <span className="text-sm font-medium">{pollingInterval} minutes</span>
                </div>
                <Slider
                  value={[pollingInterval]}
                  onValueChange={(v) => setPollingInterval(v[0])}
                  min={1}
                  max={30}
                  step={1}
                  className="w-full"
                  data-testid="polling-interval-slider"
                />
                <p className="text-xs text-muted-foreground">
                  Lower values mean more frequent checks but higher resource usage
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Paperclip className="h-5 w-5 text-primary" />
            Attachment Handling
          </CardTitle>
          <CardDescription>
            Configure how email attachments are processed
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Auto-download attachments</p>
              <p className="text-sm text-muted-foreground">Automatically download and store email attachments</p>
            </div>
            <Switch 
              checked={downloadAttachments} 
              onCheckedChange={setDownloadAttachments}
              data-testid="download-attachments-toggle"
            />
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Max attachment size</p>
              <p className="text-sm text-muted-foreground">Maximum file size to download</p>
            </div>
            <Select defaultValue="10">
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="5">5 MB</SelectItem>
                <SelectItem value="10">10 MB</SelectItem>
                <SelectItem value="25">25 MB</SelectItem>
                <SelectItem value="50">50 MB</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-primary" />
            Archive Settings
          </CardTitle>
          <CardDescription>
            Configure email archival behavior
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Auto-archive processed emails</p>
              <p className="text-sm text-muted-foreground">Move emails to archive after creating project</p>
            </div>
            <Switch defaultChecked data-testid="auto-archive-toggle" />
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Archive retention</p>
              <p className="text-sm text-muted-foreground">How long to keep archived emails</p>
            </div>
            <Select defaultValue="90">
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="30">30 days</SelectItem>
                <SelectItem value="60">60 days</SelectItem>
                <SelectItem value="90">90 days</SelectItem>
                <SelectItem value="180">180 days</SelectItem>
                <SelectItem value="365">1 year</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ============================================================================
// TEMPLATES TAB COMPONENT (from EmailTemplatesPage)
// ============================================================================
function TemplatesTab() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    template_type: "quote",
    subject: "",
    body: "",
    is_default: false,
  });

  const loadTemplates = useCallback(async () => {
    try {
      const response = await getEmailTemplates();
      setTemplates(response.data.templates || []);
    } catch (error) {
      toast.error("Failed to load templates: " + extractErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const resetForm = () => {
    setFormData({
      name: "",
      template_type: "quote",
      subject: "",
      body: "",
      is_default: false,
    });
    setEditingTemplate(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      template_type: template.template_type,
      subject: template.subject,
      body: template.body,
      is_default: template.is_default,
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (editingTemplate) {
        await updateEmailTemplate(editingTemplate.id, formData);
        toast.success("Template updated successfully");
      } else {
        await createEmailTemplate(formData);
        toast.success("Template created successfully");
      }
      setDialogOpen(false);
      resetForm();
      loadTemplates();
    } catch (error) {
      toast.error("Failed to save template: " + extractErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (template) => {
    if (!confirm(`Are you sure you want to delete "${template.name}"?`)) return;

    try {
      await deleteEmailTemplate(template.id);
      toast.success("Template deleted successfully");
      loadTemplates();
    } catch (error) {
      toast.error("Failed to delete template: " + extractErrorMessage(error));
    }
  };

  const handleSeedDefaults = async () => {
    if (!confirm("This will create default templates. Continue?")) return;
    
    setSeeding(true);
    try {
      await seedDefaultEmailTemplates();
      toast.success("Default templates created successfully");
      loadTemplates();
    } catch (error) {
      toast.error("Failed to seed templates: " + extractErrorMessage(error));
    } finally {
      setSeeding(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <p className="text-sm text-muted-foreground">
            Manage email templates for quotes, invoices, and reminders
          </p>
        </div>
        <div className="flex gap-2">
          {templates.length === 0 && (
            <Button variant="outline" onClick={handleSeedDefaults} disabled={seeding}>
              {seeding && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Seed Defaults
            </Button>
          )}
          <Button onClick={openCreateDialog}>
            <Plus className="h-4 w-4 mr-2" />
            New Template
          </Button>
        </div>
      </div>

      {templates.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <FileText className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No templates yet</h3>
            <p className="text-muted-foreground text-sm mb-4">
              Create templates for your email communications
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {templates.map((template) => (
            <Card key={template.id}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-md bg-primary/10">
                      <FileText className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <CardTitle className="text-base flex items-center gap-2">
                        {template.name}
                        {template.is_default && (
                          <Badge variant="secondary" className="text-xs">
                            <Star className="h-3 w-3 mr-1" />
                            Default
                          </Badge>
                        )}
                      </CardTitle>
                      <CardDescription className="text-xs">
                        {TEMPLATE_TYPES.find(t => t.value === template.template_type)?.label}
                      </CardDescription>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="bg-surface-elevated rounded-md p-3 mb-4">
                  <p className="text-sm font-medium mb-1">Subject:</p>
                  <p className="text-sm text-muted-foreground">{template.subject}</p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => openEditDialog(template)}>
                    <Pencil className="h-3 w-3 mr-1" />
                    Edit
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:text-destructive"
                    onClick={() => handleDelete(template)}
                  >
                    <Trash2 className="h-3 w-3 mr-1" />
                    Delete
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Template Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingTemplate ? "Edit Template" : "Create Template"}
            </DialogTitle>
            <DialogDescription>
              Configure your email template with variables for dynamic content
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Template Name</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Standard Quote"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Type</Label>
                  <Select
                    value={formData.template_type}
                    onValueChange={(v) => setFormData({ ...formData, template_type: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TEMPLATE_TYPES.map((t) => (
                        <SelectItem key={t.value} value={t.value}>
                          {t.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Subject Line</Label>
                <Input
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  placeholder="e.g., Your {{document_number}} from {{company_name}}"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label>Email Body</Label>
                <Textarea
                  value={formData.body}
                  onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                  placeholder="Enter your email content with {{variables}}"
                  rows={8}
                  required
                />
              </div>

              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.is_default}
                  onCheckedChange={(v) => setFormData({ ...formData, is_default: v })}
                />
                <Label>Set as default for this type</Label>
              </div>

              <div className="bg-surface-elevated rounded-md p-3">
                <p className="text-sm font-medium mb-2">Available Variables:</p>
                <div className="flex flex-wrap gap-2">
                  {TEMPLATE_VARIABLES.map((v) => (
                    <Badge key={v.name} variant="outline" className="text-xs cursor-help" title={v.description}>
                      {"{{" + v.name + "}}"}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editingTemplate ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================
export default function CompanyEmailPage({ isBetaMode = false }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "accounts";
  const [activeTab, setActiveTab] = useState(initialTab);
  
  // Email providers state
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProvider, setEditingProvider] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [testingProvider, setTestingProvider] = useState(null);
  const [testEmail, setTestEmail] = useState("");
  const [sendingTest, setSendingTest] = useState(false);
  // EVENTSFLO-EMAIL-SAFETY-AUDIT-LOG-AND-ONE-CLICK-TEST-V1
  // Track the last test-send result so we can offer the inline
  // "Add to allowed test recipients and send again" recovery when
  // the outcome was BLOCKED because of Email Safety Mode.
  const [lastTestResult, setLastTestResult] = useState(null);

  // EVENTSFLO-EMAIL-SAFETY-VISIBLE-AND-TEST-SEND-FIX-V1
  // Live safety posture surfaced in a dedicated card + a warning
  // banner. Refreshed on mount, after every safety update and after
  // every test send.
  const [safetyState, setSafetyState] = useState(null);
  const [safetyLoading, setSafetyLoading] = useState(true);
  const [savingSafety, setSavingSafety] = useState(false);
  const [allowlistInput, setAllowlistInput] = useState("");

  const loadSafetyState = useCallback(async () => {
    try {
      const resp = await api.get("/settings/email/safety");
      setSafetyState(resp.data || null);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[EmailSafety] failed to load:", err);
    } finally {
      setSafetyLoading(false);
    }
  }, []);

  // EVENTSFLO-EMAIL-SAFETY-RECENT-CHANGES-TABLE-V1
  // Load the latest 10 audit rows. Failure is soft — the whole page
  // must still render normally if the audit endpoint hiccups.
  const [safetyAuditLog, setSafetyAuditLog] = useState({
    entries: null,   // null → loading, [] → empty, [row,…] → data
    error: false,
  });

  const loadSafetyAuditLog = useCallback(async () => {
    try {
      const resp = await api.get(
        "/settings/email/safety/audit-log?limit=10",
      );
      const entries = Array.isArray(resp?.data?.entries)
        ? resp.data.entries
        : [];
      setSafetyAuditLog({ entries, error: false });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[EmailSafety] audit-log load failed:", err);
      setSafetyAuditLog({ entries: [], error: true });
    }
  }, []);

  const persistSafety = async (patch) => {
    setSavingSafety(true);
    try {
      const resp = await api.put("/settings/email/safety", patch);
      setSafetyState(resp.data || null);
      toast.success("Email Safety updated.");
      // EVENTSFLO-EMAIL-SAFETY-RECENT-CHANGES-TABLE-V1: reload table.
      loadSafetyAuditLog();
    } catch (err) {
      toast.error("Failed to update Email Safety: " + extractErrorMessage(err));
    } finally {
      setSavingSafety(false);
    }
  };

  const addAllowlistRecipient = async () => {
    const v = (allowlistInput || "").trim().toLowerCase();
    if (!v) return;
    const current = safetyState?.allowed_direct_recipients || [];
    if (current.includes(v)) {
      toast.info("Already on the allowlist.");
      return;
    }
    await persistSafety({ allowed_direct_recipients: [...current, v] });
    setAllowlistInput("");
  };

  const removeAllowlistRecipient = async (addr) => {
    const current = safetyState?.allowed_direct_recipients || [];
    await persistSafety({
      allowed_direct_recipients: current.filter((e) => e !== addr),
    });
  };

  const [formData, setFormData] = useState({
    name: "",
    provider_type: "google_smtp",
    from_name: "",
    from_email: "",
    reply_to_email: "",
    enabled: true,
    is_default: false,
    api_key: "",
    smtp_host: "",
    smtp_port: 587,
    smtp_username: "",
    smtp_password: "",
    smtp_use_tls: true,
    imap_host: "",
    imap_port: 993,
    imap_username: "",
    imap_password: "",
    imap_folder: "INBOX",
    imap_use_ssl: true,
  });

  // Update URL when tab changes
  const handleTabChange = (value) => {
    setActiveTab(value);
    setSearchParams({ tab: value });
  };

  // Load providers
  const loadProviders = useCallback(async () => {
    try {
      const response = await api.get("/settings/email/providers");
      // EF-SETTINGS-CONSOLIDATION-V1 (PHASE 4.5): Handle both array and object response
      const providersData = Array.isArray(response.data) ? response.data : (response.data.providers || []);
      setProviders(providersData);
    } catch (error) {
      toast.error("Failed to load email providers: " + extractErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProviders();
    // EVENTSFLO-EMAIL-SAFETY-VISIBLE-AND-TEST-SEND-FIX-V1
    loadSafetyState();
    // EVENTSFLO-EMAIL-SAFETY-RECENT-CHANGES-TABLE-V1
    loadSafetyAuditLog();
  }, [loadProviders, loadSafetyState, loadSafetyAuditLog]);

  // Provider CRUD operations
  const resetForm = () => {
    setFormData({
      name: "",
      provider_type: "google_smtp",
      from_name: "",
      from_email: "",
      reply_to_email: "",
      enabled: true,
      is_default: false,
      api_key: "",
      smtp_host: "smtp.gmail.com",
      smtp_port: 587,
      smtp_username: "",
      smtp_password: "",
      smtp_use_tls: true,
      imap_host: "imap.gmail.com",
      imap_port: 993,
      imap_username: "",
      imap_password: "",
      imap_folder: "INBOX",
      imap_use_ssl: true,
    });
    setEditingProvider(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (provider) => {
    setEditingProvider(provider);
    setFormData({
      name: provider.name,
      provider_type: provider.provider_type,
      from_name: provider.from_name,
      from_email: provider.from_email,
      reply_to_email: provider.reply_to_email || "",
      enabled: provider.enabled,
      is_default: provider.is_default,
      api_key: "",
      smtp_host: provider.smtp_host || "",
      smtp_port: provider.smtp_port || 587,
      smtp_username: provider.smtp_username || "",
      smtp_password: "",
      smtp_use_tls: provider.smtp_use_tls ?? true,
      imap_host: provider.imap_host || "",
      imap_port: provider.imap_port || 993,
      imap_username: provider.imap_username || "",
      imap_password: "",
      imap_folder: provider.imap_folder || "INBOX",
      imap_use_ssl: provider.imap_use_ssl ?? true,
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const payload = { ...formData };
      
      // Remove empty optional fields
      if (!payload.reply_to_email) delete payload.reply_to_email;
      
      // Handle provider-specific fields
      if (payload.provider_type === "smtp" || payload.provider_type === "google_smtp") {
        delete payload.api_key;
        if (!payload.smtp_password && editingProvider) {
          delete payload.smtp_password;
        }
        if (!payload.imap_password && editingProvider) {
          delete payload.imap_password;
        }
      } else {
        delete payload.smtp_host;
        delete payload.smtp_port;
        delete payload.smtp_username;
        delete payload.smtp_password;
        delete payload.smtp_use_tls;
        delete payload.imap_host;
        delete payload.imap_port;
        delete payload.imap_username;
        delete payload.imap_password;
        delete payload.imap_folder;
        delete payload.imap_use_ssl;
        if (!payload.api_key && editingProvider) {
          delete payload.api_key;
        }
      }

      if (editingProvider) {
        await api.put(`/settings/email/providers/${editingProvider.id}`, payload);
        toast.success("Provider updated successfully");
      } else {
        await api.post("/settings/email/providers", payload);
        toast.success("Provider created successfully");
      }

      setDialogOpen(false);
      resetForm();
      loadProviders();
    } catch (error) {
      toast.error("Failed to save provider: " + extractErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (provider) => {
    if (!confirm(`Are you sure you want to delete "${provider.name}"?`)) return;

    try {
      await api.delete(`/settings/email/providers/${provider.id}`);
      toast.success("Provider deleted successfully");
      loadProviders();
    } catch (error) {
      toast.error("Failed to delete provider: " + extractErrorMessage(error));
    }
  };

  const handleToggleEnabled = async (provider) => {
    try {
      await api.put(`/settings/email/providers/${provider.id}`, {
        ...provider,
        enabled: !provider.enabled,
      });
      loadProviders();
    } catch (error) {
      toast.error("Failed to update provider: " + extractErrorMessage(error));
    }
  };

  const handleSetDefault = async (provider) => {
    try {
      await api.put(`/settings/email/providers/${provider.id}`, {
        ...provider,
        is_default: true,
      });
      toast.success(`${provider.name} is now the default sender`);
      loadProviders();
    } catch (error) {
      toast.error("Failed to set default: " + extractErrorMessage(error));
    }
  };

  const openTestDialog = (provider) => {
    setTestingProvider(provider);
    setTestEmail("");
    setLastTestResult(null); // EVENTSFLO-EMAIL-SAFETY-AUDIT-LOG-AND-ONE-CLICK-TEST-V1
    setTestDialogOpen(true);
  };

  const handleSendTest = async () => {
    if (!testEmail) {
      toast.error("Please enter a test email address");
      return;
    }
    setLastTestResult(null);
    setSendingTest(true);
    try {
      // EVENTSFLO-EMAIL-SAFETY-VISIBLE-AND-TEST-SEND-FIX-V1
      // The endpoint now returns a normalised envelope with
      // `status ∈ {SENT, REDIRECTED, BLOCKED, FAILED}`. Show the
      // exact outcome — never say "sent" when the email was blocked
      // or redirected.
      const resp = await api.post(
        `/settings/email/providers/${testingProvider.id}/test`,
        { to_email: testEmail },
      );
      const data = resp?.data || {};
      // Remember the result so the recovery block below can react.
      setLastTestResult({ ...data, target: testEmail });
      const status = String(data.status || "").toUpperCase();
      if (status === "SENT") {
        toast.success(
          data.message ||
            `Email sent successfully to ${data.actual_to || testEmail}.`,
        );
        setTestDialogOpen(false);
      } else if (status === "BLOCKED") {
        toast.error(
          data.message ||
            `Test email was NOT sent to ${testEmail}. Email Safety Mode is active. Turn off Email Safety Mode or add this address to allowed direct test recipients.`,
          { duration: 10000 },
        );
      } else if (status === "REDIRECTED") {
        toast.error(
          data.message ||
            `Email was NOT sent to ${testEmail}. Preview Email Safety Mode redirected it to ${data.actual_to}.`,
          { duration: 10000 },
        );
      } else {
        toast.error(
          data.message ||
            `Email failed. Provider returned: ${data.provider_error || "unknown"}. Check API key, sender domain, SMTP credentials, provider limits, or verified sender configuration.`,
          { duration: 10000 },
        );
      }
      // Refresh the safety card so the operator sees the up-to-date
      // posture after this test send.
      loadSafetyState();
    } catch (error) {
      toast.error("Failed to send test email: " + extractErrorMessage(error));
    } finally {
      setSendingTest(false);
    }
  };

  // EVENTSFLO-EMAIL-SAFETY-AUDIT-LOG-AND-ONE-CLICK-TEST-V1
  // One-click recovery when a Test Send was BLOCKED because the
  // recipient is not on the allowlist. Adds the recipient, waits
  // for the safety state to persist, then immediately retries.
  const handleAddAllowlistedAndRetry = async () => {
    if (!lastTestResult?.target || !testingProvider?.id) return;
    const target = (lastTestResult.target || "").trim().toLowerCase();
    if (!target) return;
    setSendingTest(true);
    try {
      const current = safetyState?.allowed_direct_recipients || [];
      const next = current.includes(target) ? current : [...current, target];
      await api.put(
        "/settings/email/safety",
        { allowed_direct_recipients: next },
        { headers: { "X-EventsFlo-Source": "settings/email/test-send-recovery" } },
      );
      // Reload safety state so the card + banner reflect it and the
      // resolver cache is warm.
      await loadSafetyState();
      // EVENTSFLO-EMAIL-SAFETY-RECENT-CHANGES-TABLE-V1
      loadSafetyAuditLog();
      // Retry the actual send. Reuse the exact requested_to so the
      // envelope compare stays honest.
      const resp = await api.post(
        `/settings/email/providers/${testingProvider.id}/test`,
        { to_email: lastTestResult.target },
      );
      const data = resp?.data || {};
      setLastTestResult({ ...data, target: lastTestResult.target });
      const status = String(data.status || "").toUpperCase();
      if (status === "SENT") {
        toast.success(
          data.message ||
            `Email sent successfully to ${data.actual_to || lastTestResult.target}.`,
        );
        setTestDialogOpen(false);
      } else if (status === "BLOCKED") {
        toast.error(
          data.message ||
            `Retry still blocked. Check Email Safety Mode configuration.`,
          { duration: 10000 },
        );
      } else {
        toast.error(
          data.message ||
            `Email failed. Provider returned: ${data.provider_error || "unknown"}.`,
          { duration: 10000 },
        );
      }
    } catch (err) {
      toast.error("One-click retry failed: " + extractErrorMessage(err));
    } finally {
      setSendingTest(false);
    }
  };

  const selectedProviderType = PROVIDER_TYPES.find(t => t.value === formData.provider_type);

  return (
    <div className="space-y-6" data-testid="company-email-page">
      {/* EVENTSFLO-EMAIL-SAFETY-VISIBLE-AND-TEST-SEND-FIX-V1 — banner */}
      {safetyState?.safety_mode_enabled && (
        <Alert
          className="border-amber-500/60 bg-amber-500/10"
          data-testid="email-safety-banner"
        >
          <AlertCircle className="h-4 w-4 text-amber-500" />
          <AlertTitle className="text-amber-500">
            Email Safety Mode is ON
          </AlertTitle>
          <AlertDescription className="text-amber-400">
            Emails may not be delivered to the intended recipient. Some
            emails may be redirected to{" "}
            <span className="font-mono">{safetyState?.redirect_target || "the safety inbox"}</span>{" "}
            or blocked. Add specific addresses to the allowlist below to
            allow direct Test Send delivery.
          </AlertDescription>
        </Alert>
      )}
      {/* EF-SETTINGS-CONSOLIDATION-V1 (PHASE 2A): Beta Banner when accessed via force_new */}
      {isBetaMode && (
        <Alert className="border-amber-500/50 bg-amber-500/10">
          <AlertCircle className="h-4 w-4 text-amber-500" />
          <AlertTitle className="text-amber-500">New Email Settings (Beta)</AlertTitle>
          <AlertDescription className="text-amber-400">
            You are using the new unified email configuration. This is a preview of the upcoming UI consolidation.
            <a href="/settings/email" className="ml-2 underline hover:text-amber-300">
              Switch to current version
            </a>
          </AlertDescription>
        </Alert>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
            <a href="/settings" className="hover:text-foreground">Settings</a>
            <ChevronRight className="h-4 w-4" />
            <a href="/settings" className="hover:text-foreground">Company</a>
            <ChevronRight className="h-4 w-4" />
            <span className="text-foreground">Email Configuration</span>
          </div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Mail className="h-6 w-6 text-primary" />
            Email Configuration
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Manage email providers, templates, and inbox settings
          </p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
        <TabsList className="grid w-full grid-cols-4 lg:w-[600px]">
          <TabsTrigger value="accounts" className="gap-2" data-testid="tab-accounts">
            <Settings className="h-4 w-4" />
            <span className="hidden sm:inline">Accounts</span>
          </TabsTrigger>
          <TabsTrigger value="sending" className="gap-2" data-testid="tab-sending">
            <Send className="h-4 w-4" />
            <span className="hidden sm:inline">Sending</span>
          </TabsTrigger>
          <TabsTrigger value="inbox" className="gap-2" data-testid="tab-inbox">
            <Inbox className="h-4 w-4" />
            <span className="hidden sm:inline">Inbox</span>
          </TabsTrigger>
          <TabsTrigger value="templates" className="gap-2" data-testid="tab-templates">
            <FileText className="h-4 w-4" />
            <span className="hidden sm:inline">Templates</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="accounts">
          {/* EVENTSFLO-EMAIL-SAFETY-VISIBLE-AND-TEST-SEND-FIX-V1 — card */}
          <div
            className="mb-6 border rounded-lg p-4 bg-muted/20"
            data-testid="email-safety-card"
          >
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="font-semibold flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-amber-500" />
                  Email Safety
                </h3>
                <p className="text-xs text-muted-foreground mt-1">
                  Controls whether outbound emails go to their real
                  recipients. Redirect target and environment are
                  configured at deploy time.
                </p>
              </div>
              <div className="flex items-center gap-2 text-xs">
                <span
                  className={
                    "px-2 py-0.5 rounded-full font-mono " +
                    (safetyState?.environment === "production"
                      ? "bg-green-500/15 text-green-500"
                      : "bg-amber-500/15 text-amber-500")
                  }
                  data-testid="email-safety-environment"
                >
                  {safetyLoading ? "…" : (safetyState?.environment || "unknown")}
                </span>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div className="flex items-center justify-between border rounded p-2">
                <div>
                  <div className="font-medium">Email sending enabled</div>
                  <div className="text-xs text-muted-foreground">Master switch</div>
                </div>
                <div
                  className={
                    "font-mono " +
                    (safetyState?.email_sending_enabled
                      ? "text-green-500"
                      : "text-red-500")
                  }
                  data-testid="email-safety-sending-status"
                >
                  {safetyState?.email_sending_enabled ? "ON" : "OFF"}
                </div>
              </div>
              <div className="flex items-center justify-between border rounded p-2">
                <div>
                  <div className="font-medium">Email Safety Mode</div>
                  <div className="text-xs text-muted-foreground">
                    When ON, outbound emails may be redirected.
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() =>
                    persistSafety({
                      safety_mode_enabled: !safetyState?.safety_mode_enabled,
                    })
                  }
                  disabled={savingSafety}
                  className={
                    "text-xs px-3 py-1 rounded-full font-mono border " +
                    (safetyState?.safety_mode_enabled
                      ? "bg-amber-500/15 text-amber-500 border-amber-500/50"
                      : "bg-green-500/15 text-green-500 border-green-500/50")
                  }
                  data-testid="email-safety-mode-toggle"
                >
                  {safetyState?.safety_mode_enabled ? "ON — click to turn OFF" : "OFF — click to turn ON"}
                </button>
              </div>
              <div className="border rounded p-2">
                <div className="font-medium">Safety redirect inbox</div>
                <div
                  className="text-xs text-muted-foreground font-mono break-all"
                  data-testid="email-safety-redirect-target"
                >
                  {safetyState?.redirect_target || "(not configured)"}
                </div>
              </div>
              <div className="border rounded p-2">
                <div className="font-medium">Email provider connection</div>
                <div
                  className={
                    "text-xs font-mono " +
                    (safetyState?.provider_configured
                      ? "text-green-500"
                      : "text-red-500")
                  }
                  data-testid="email-safety-provider-status"
                >
                  {safetyState?.provider_configured ? "Configured" : "Not configured"}
                </div>
              </div>
            </div>
            <div className="mt-4">
              <div className="font-medium text-sm mb-1">
                Allowed direct test recipients
              </div>
              <div className="text-xs text-muted-foreground mb-2">
                Even when Email Safety Mode is ON, addresses on this
                list receive Test Send emails directly (no redirect).
                Use an entry like <span className="font-mono">@hillsdon.com</span> to allow a whole domain.
              </div>
              <div className="flex gap-2">
                <input
                  type="email"
                  value={allowlistInput}
                  onChange={(e) => setAllowlistInput(e.target.value)}
                  placeholder="e.g. mark@hillsdon.com or @hillsdon.com"
                  className="flex-1 border rounded px-2 py-1 text-sm bg-background"
                  data-testid="email-safety-allowlist-input"
                />
                <button
                  type="button"
                  onClick={addAllowlistRecipient}
                  disabled={savingSafety || !allowlistInput.trim()}
                  className="text-sm border rounded px-3 py-1 bg-primary text-primary-foreground"
                  data-testid="email-safety-allowlist-add"
                >
                  Add
                </button>
              </div>
              {(safetyState?.allowed_direct_recipients || []).length > 0 && (
                <ul
                  className="mt-2 flex flex-wrap gap-2 text-xs"
                  data-testid="email-safety-allowlist"
                >
                  {(safetyState?.allowed_direct_recipients || []).map((addr) => (
                    <li
                      key={addr}
                      className="flex items-center gap-2 border rounded-full px-2 py-1"
                      data-testid={`email-safety-allowlist-entry-${addr}`}
                    >
                      <span className="font-mono">{addr}</span>
                      <button
                        type="button"
                        className="text-red-500 hover:text-red-400"
                        onClick={() => removeAllowlistRecipient(addr)}
                        disabled={savingSafety}
                        data-testid={`email-safety-allowlist-remove-${addr}`}
                      >
                        ✕
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* EVENTSFLO-EMAIL-SAFETY-RECENT-CHANGES-TABLE-V1 */}
            <div
              className="mt-4 pt-4 border-t"
              data-testid="email-safety-audit-log"
            >
              <div className="font-medium text-sm mb-2">
                Recent Email Safety changes
              </div>
              {safetyAuditLog.error && (
                <div
                  className="text-xs text-amber-500 mb-2"
                  data-testid="email-safety-audit-log-error"
                >
                  Recent Email Safety changes could not be loaded.
                </div>
              )}
              {!safetyAuditLog.error && safetyAuditLog.entries === null && (
                <div className="text-xs text-muted-foreground">Loading…</div>
              )}
              {!safetyAuditLog.error &&
                Array.isArray(safetyAuditLog.entries) &&
                safetyAuditLog.entries.length === 0 && (
                  <div
                    className="text-xs text-muted-foreground"
                    data-testid="email-safety-audit-log-empty"
                  >
                    No recent Email Safety changes.
                  </div>
                )}
              {!safetyAuditLog.error &&
                Array.isArray(safetyAuditLog.entries) &&
                safetyAuditLog.entries.length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs border-collapse">
                      <thead>
                        <tr className="text-left text-muted-foreground border-b">
                          <th className="py-1 pr-3">When</th>
                          <th className="py-1 pr-3">Actor</th>
                          <th className="py-1 pr-3">Setting</th>
                          <th className="py-1 pr-3">Previous</th>
                          <th className="py-1 pr-3">New</th>
                          <th className="py-1 pr-3">Env</th>
                          <th className="py-1">Source</th>
                        </tr>
                      </thead>
                      <tbody>
                        {safetyAuditLog.entries.map((row) => {
                          const fmt = (v) => {
                            if (v === null || v === undefined) return "—";
                            if (Array.isArray(v)) {
                              return v.length ? v.join(", ") : "[]";
                            }
                            if (typeof v === "boolean") return v ? "ON" : "OFF";
                            return String(v);
                          };
                          const when = row.timestamp
                            ? new Date(row.timestamp).toLocaleString()
                            : "—";
                          return (
                            <tr
                              key={row.id || `${row.timestamp}-${row.setting_changed}`}
                              className="border-b border-border/40"
                              data-testid="email-safety-audit-log-row"
                            >
                              <td className="py-1 pr-3 whitespace-nowrap font-mono">
                                {when}
                              </td>
                              <td className="py-1 pr-3 break-all">
                                {row.actor_email || "unknown"}
                              </td>
                              <td className="py-1 pr-3 font-mono">
                                {row.setting_changed}
                              </td>
                              <td className="py-1 pr-3 break-all">
                                {fmt(row.previous_value)}
                              </td>
                              <td className="py-1 pr-3 break-all">
                                {fmt(row.new_value)}
                              </td>
                              <td className="py-1 pr-3 font-mono">
                                {row.environment || "—"}
                              </td>
                              <td className="py-1 break-all">
                                {row.source || "—"}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
            </div>
          </div>

          <AccountsTab
            providers={providers}
            loading={loading}
            onRefresh={loadProviders}
            onCreateProvider={openCreateDialog}
            onEditProvider={openEditDialog}
            onDeleteProvider={handleDelete}
            onToggleEnabled={handleToggleEnabled}
            onTestProvider={openTestDialog}
            onSetDefault={handleSetDefault}
          />
        </TabsContent>

        <TabsContent value="sending">
          <SendingDefaultsTab
            providers={providers}
            loading={loading}
            onRefresh={loadProviders}
          />
        </TabsContent>

        <TabsContent value="inbox">
          <InboxSettingsTab />
        </TabsContent>

        <TabsContent value="templates">
          <TemplatesTab />
        </TabsContent>
      </Tabs>

      {/* Provider Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingProvider ? "Edit Email Provider" : "Add Email Provider"}
            </DialogTitle>
            <DialogDescription>
              Configure an email provider for sending notifications and documents
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Provider Name</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Main Gmail Account"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Provider Type</Label>
                  <Select
                    value={formData.provider_type}
                    onValueChange={(v) => {
                      const type = PROVIDER_TYPES.find(t => t.value === v);
                      setFormData({
                        ...formData,
                        provider_type: v,
                        smtp_host: v === "google_smtp" ? "smtp.gmail.com" : "",
                        imap_host: v === "google_smtp" ? "imap.gmail.com" : "",
                      });
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PROVIDER_TYPES.map((t) => (
                        <SelectItem key={t.value} value={t.value}>
                          {t.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* From Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>From Name</Label>
                  <Input
                    value={formData.from_name}
                    onChange={(e) => setFormData({ ...formData, from_name: e.target.value })}
                    placeholder="e.g., Your Company"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>From Email</Label>
                  <Input
                    type="email"
                    value={formData.from_email}
                    onChange={(e) => setFormData({ ...formData, from_email: e.target.value })}
                    placeholder="e.g., noreply@company.com"
                    required
                  />
                </div>
              </div>

              {/* SMTP Settings */}
              {selectedProviderType?.hasSMTP && (
                <>
                  <div className="pt-4 border-t">
                    <h4 className="font-medium mb-3 flex items-center gap-2">
                      <Send className="h-4 w-4" />
                      SMTP Settings (Sending)
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>SMTP Host</Label>
                        <Input
                          value={formData.smtp_host}
                          onChange={(e) => setFormData({ ...formData, smtp_host: e.target.value })}
                          placeholder="smtp.gmail.com"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>SMTP Port</Label>
                        <Input
                          type="number"
                          value={formData.smtp_port}
                          onChange={(e) => setFormData({ ...formData, smtp_port: parseInt(e.target.value) })}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>SMTP Username</Label>
                        <Input
                          value={formData.smtp_username}
                          onChange={(e) => setFormData({ ...formData, smtp_username: e.target.value })}
                          placeholder="your-email@gmail.com"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>SMTP Password / App Password</Label>
                        <Input
                          type="password"
                          value={formData.smtp_password}
                          onChange={(e) => setFormData({ ...formData, smtp_password: e.target.value })}
                          placeholder={editingProvider ? "Leave empty to keep current" : "••••••••"}
                          required={!editingProvider}
                        />
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-3">
                      <Switch
                        checked={formData.smtp_use_tls}
                        onCheckedChange={(v) => setFormData({ ...formData, smtp_use_tls: v })}
                      />
                      <Label>Use TLS</Label>
                    </div>
                  </div>

                  {/* IMAP Settings */}
                  {selectedProviderType?.hasImap && (
                    <div className="pt-4 border-t">
                      <h4 className="font-medium mb-3 flex items-center gap-2">
                        <Inbox className="h-4 w-4" />
                        IMAP Settings (Receiving)
                      </h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>IMAP Host</Label>
                          <Input
                            value={formData.imap_host}
                            onChange={(e) => setFormData({ ...formData, imap_host: e.target.value })}
                            placeholder="imap.gmail.com"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>IMAP Port</Label>
                          <Input
                            type="number"
                            value={formData.imap_port}
                            onChange={(e) => setFormData({ ...formData, imap_port: parseInt(e.target.value) })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>IMAP Username</Label>
                          <Input
                            value={formData.imap_username}
                            onChange={(e) => setFormData({ ...formData, imap_username: e.target.value })}
                            placeholder="your-email@gmail.com"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>IMAP Password / App Password</Label>
                          <Input
                            type="password"
                            value={formData.imap_password}
                            onChange={(e) => setFormData({ ...formData, imap_password: e.target.value })}
                            placeholder={editingProvider ? "Leave empty to keep current" : "••••••••"}
                          />
                        </div>
                      </div>
                      <div className="flex items-center gap-2 mt-3">
                        <Switch
                          checked={formData.imap_use_ssl}
                          onCheckedChange={(v) => setFormData({ ...formData, imap_use_ssl: v })}
                        />
                        <Label>Use SSL</Label>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* API Key for non-SMTP providers */}
              {selectedProviderType?.hasApiKey && (
                <div className="space-y-2">
                  <Label>API Key</Label>
                  <Input
                    type="password"
                    value={formData.api_key}
                    onChange={(e) => setFormData({ ...formData, api_key: e.target.value })}
                    placeholder={editingProvider ? "Leave empty to keep current" : "••••••••"}
                    required={!editingProvider}
                  />
                </div>
              )}

              {/* Options */}
              <div className="flex items-center gap-6 pt-4 border-t">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={formData.enabled}
                    onCheckedChange={(v) => setFormData({ ...formData, enabled: v })}
                  />
                  <Label>Enabled</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={formData.is_default}
                    onCheckedChange={(v) => setFormData({ ...formData, is_default: v })}
                  />
                  <Label>Set as default sender</Label>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editingProvider ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Test Email Dialog */}
      <Dialog open={testDialogOpen} onOpenChange={setTestDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Test Email</DialogTitle>
            <DialogDescription>
              Send a test email using {testingProvider?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Recipient Email</Label>
              <Input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="test@example.com"
                data-testid="test-send-recipient-input"
              />
            </div>
            {/* EVENTSFLO-EMAIL-SAFETY-AUDIT-LOG-AND-ONE-CLICK-TEST-V1
                Inline result readout with one-click recovery when
                the last send was BLOCKED because Email Safety Mode
                is on and the target isn't on the allowlist. */}
            {lastTestResult && (
              <div
                className={
                  "rounded-md border p-3 text-sm " +
                  (lastTestResult.status === "SENT"
                    ? "border-green-500/40 bg-green-500/10 text-green-500"
                    : "border-amber-500/40 bg-amber-500/10 text-amber-500")
                }
                data-testid="test-send-result"
              >
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="font-medium">
                      {lastTestResult.status || "UNKNOWN"}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1 break-words">
                      {lastTestResult.message}
                    </div>
                  </div>
                </div>
                {lastTestResult.status === "BLOCKED" && (
                  <div className="mt-3">
                    <Button
                      size="sm"
                      onClick={handleAddAllowlistedAndRetry}
                      disabled={sendingTest}
                      data-testid="test-send-add-and-retry"
                    >
                      {sendingTest && <Loader2 className="h-3 w-3 mr-2 animate-spin" />}
                      Add to allowed test recipients and send again
                    </Button>
                    <p className="text-xs text-muted-foreground mt-2">
                      This adds{" "}
                      <span className="font-mono">{lastTestResult.target}</span>{" "}
                      to the allowlist and immediately retries the send.
                      An audit-log entry is recorded.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTestDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSendTest} disabled={sendingTest}>
              {sendingTest && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Send Test
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
